import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import {
  AGE_MAP,
  DATA_KEY,
  DESTINATION_MAP,
  GOAL_MAP,
  SAVINGS_MAP,
  SESSION_ID_KEY,
  SITUATION_MAP,
  TIMELINE_MAP,
  toEnum,
} from '@/contexts/OnboardingContext'
import type { ApiUser } from '@/lib/api'
import {
  claimOnboardingSession,
  createUser,
  getMe,
  patchOnboardingSession,
  setAccessToken,
} from '@/lib/api'
import {
  getTokenProfile,
  type AdminSignInResult,
  type AuthMethod,
  type MfaChallenge,
  type StoredTokens,
} from '@/lib/cognito'
import type { GoogleOAuthCallbackParams } from '@/lib/cognito-oauth'
import { storage } from '@/lib/storage'
import { useAnalytics } from '../hooks/useAnalytics'

export type { AdminSignInResult, GoogleOAuthCallbackParams, MfaChallenge }

const ts = () => new Date().toISOString()

/** Map a raw string value to its enum equivalent, or undefined if falsy. */
const mapEnum = (raw: string | null | undefined, map: Record<string, string>) =>
  raw ? toEnum(map, raw) : undefined

interface SignInOptions {
  email: string
  password: string
  name?: string
  surname?: string
  phone?: string
}

type LocalOnboardingData = {
  goal?: string | null
  destination?: string | null
  timeline?: string | null
  originCity?: string | null
  originCountry?: string | null
  age?: string | null
  savings?: string | null
  situation?: string | null
}

type CompleteSessionOptions = {
  tokens: StoredTokens
  email?: string | null
  name?: string | null
  surname?: string | null
  phone?: string
}

type ResolveSessionUserOptions = {
  emailFromSession: string | null | undefined
  name?: string
  surname?: string
  phone?: string
}

type E2EApiState = {
  usersById?: Record<string, ApiUser>
  userIdByEmail?: Record<string, string>
}

type GoogleSignInResult =
  { type: 'cancelled' } | { type: 'redirected' } | { type: 'success'; user: ApiUser }

function getErrorStatus(err: unknown): number | null {
  if (!err || typeof err !== 'object') return null
  const status = (err as { status?: unknown }).status
  return typeof status === 'number' ? status : null
}

export function getIsAdmin(accessToken: string | null): boolean {
  if (!accessToken) return false
  try {
    const raw = accessToken.split('.')[1]
    const base64 = raw.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
    const payload = JSON.parse(atob(padded))
    const groups: string[] = payload['cognito:groups'] ?? []
    return groups.includes('admins')
  } catch {
    return false
  }
}

const TOKEN_KEYS = {
  accessToken: 'turbina_access_token',
  idToken: 'turbina_id_token',
  refreshToken: 'turbina_refresh_token',
  username: 'turbina_cognito_username',
  authMethod: 'turbina_auth_method',
} as const

function normalizeStoredUsername(value: string | null): string | null {
  const normalized = value?.trim()
  return normalized ? normalized : null
}

function toAuthMethod(value: string | null): AuthMethod {
  if (value === 'google' || value === 'e2e' || value === 'password') {
    return value
  }
  return 'password'
}

async function readStoredTokensLight(): Promise<StoredTokens | null> {
  const [accessToken, idToken, refreshToken, username, authMethodRaw] = await Promise.all([
    storage.get(TOKEN_KEYS.accessToken),
    storage.get(TOKEN_KEYS.idToken),
    storage.get(TOKEN_KEYS.refreshToken),
    storage.get(TOKEN_KEYS.username),
    storage.get(TOKEN_KEYS.authMethod),
  ])

  if (!accessToken || !idToken || !refreshToken) return null

  return {
    accessToken,
    idToken,
    refreshToken,
    username: normalizeStoredUsername(username),
    authMethod: toAuthMethod(authMethodRaw),
  }
}

async function clearStoredTokensLight(): Promise<void> {
  await Promise.all(Object.values(TOKEN_KEYS).map((key) => storage.remove(key)))
}

interface AuthState {
  user: ApiUser | null
  isLoading: boolean
  isAuthenticated: boolean
  isAdmin: boolean
  authMethod: AuthMethod | null
  signIn: (options: SignInOptions) => Promise<ApiUser>
  signInWithGoogle: () => Promise<GoogleSignInResult>
  completeGoogleCallback: (params: GoogleOAuthCallbackParams) => Promise<ApiUser>
  adminSignIn: (email: string, password: string) => Promise<AdminSignInResult>
  signUp: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  updateUser: (updated: Partial<ApiUser>) => void
}

const AuthContext = createContext<AuthState | null>(null)

function requireEmailForBootstrap(email: string | null | undefined): string {
  const normalized = email?.trim()
  if (!normalized) {
    throw new Error('Your Cognito session is missing an email address.')
  }
  return normalized
}

async function resolveE2ESeededUser(tokens: StoredTokens): Promise<ApiUser | null> {
  if (process.env.EXPO_PUBLIC_E2E !== 'true') return null

  const profile = getTokenProfile(tokens.idToken, tokens.accessToken)
  const email = profile.email?.trim().toLowerCase()
  if (!email) return null

  try {
    const raw = await storage.get('turbina_e2e_api_state')
    if (!raw) return null
    const parsed = JSON.parse(raw) as E2EApiState

    const userID = parsed.userIdByEmail?.[email]
    if (!userID) return null

    return parsed.usersById?.[userID] ?? null
  } catch {
    return null
  }
}

function logGoogleOAuthResult(
  result: {
    type: string
    tokens?: { authMethod?: string }
    profile?: { email?: string | null; name?: string | null; surname?: string | null }
  },
  captureFn: (event: string, props: Record<string, unknown>) => void
) {
  const isSuccess = result.type === 'success'
  const data = {
    type: result.type,
    hasTokens: isSuccess && !!result.tokens,
    hasProfile: isSuccess && !!result.profile,
  }
  console.info(ts(), '[auth][google] signInWithGoogle: startGoogleOAuth returned', data)
  captureFn('google_oauth_debug', { prefix: '[auth][google]', step: 'oauth_returned', ...data })
}

function logGoogleOAuthError(
  err: unknown,
  captureFn: (event: string, props: Record<string, unknown>) => void
) {
  const isError = err instanceof Error
  const errorInfo = {
    errorName: isError ? err.name : 'Unknown',
    code: (err as { code?: string }).code,
  }
  console.error(ts(), '[auth][google] signInWithGoogle: error', {
    ...errorInfo,
    error: isError ? err.message : String(err),
    stack: isError ? err.stack?.substring(0, 300) : undefined,
  })
  captureFn('google_oauth_debug', {
    prefix: '[auth][google]',
    step: 'sign_in_error',
    ...errorInfo,
  })
}

/**
 * Pre-sync local onboarding answers to the server so the auth.onboarding
 * row exists before POST /users tries to claim it.  Non-fatal — if this
 * fails the post-auth claimPendingOnboardingSession will handle it.
 */
async function preSyncOnboardingToServer(sessionId: string): Promise<void> {
  const rawData = await storage.get(DATA_KEY)
  if (!rawData) return
  try {
    const d = JSON.parse(rawData) as LocalOnboardingData
    console.info(ts(), '[auth][createUser] syncing local onboarding data to server before signup')
    await patchOnboardingSession(sessionId, {
      goal: mapEnum(d.goal, GOAL_MAP),
      destination: mapEnum(d.destination, DESTINATION_MAP),
      timeline: mapEnum(d.timeline, TIMELINE_MAP),
      origin_city: d.originCity ?? undefined,
      origin_country: d.originCountry ?? undefined,
      age_range: mapEnum(d.age, AGE_MAP),
      savings_status: mapEnum(d.savings, SAVINGS_MAP),
      current_situation: mapEnum(d.situation, SITUATION_MAP),
    })
  } catch (syncErr) {
    console.warn(ts(), '[auth][createUser] failed to pre-sync onboarding data', syncErr)
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { capture } = useAnalytics()
  const [user, setUser] = useState<ApiUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const [authMethod, setAuthMethod] = useState<AuthMethod | null>(null)

  const applySessionState = useCallback((tokens: StoredTokens) => {
    setAccessToken(tokens.accessToken)
    setIsAdmin(getIsAdmin(tokens.accessToken))
    setAuthMethod(tokens.authMethod)
  }, [])

  const captureRegisteredUser = useCallback(
    (me: ApiUser) => {
      capture('user_registered', {
        origin_country: me.origin_country,
        onboarding_goal: me.onboarding_goal,
      })
    },
    [capture]
  )

  const createUserWithGetMeFallback = useCallback(
    async (options: {
      email: string
      name?: string
      surname?: string
      phone?: string
      modeLabel: string
    }): Promise<ApiUser> => {
      const { email, name, surname, phone, modeLabel } = options
      const _tCreate = Date.now()

      // Attach onboarding session at signup so the backend can link it
      // during CreateUser — avoids a separate claim round-trip.
      const onboardingSessionId = await storage.get(SESSION_ID_KEY)
      console.info(ts(), `[auth][createUser] onboardingSessionId=${onboardingSessionId ?? 'none'}`)

      // Sync local onboarding data to the server before creating the user
      // so the row exists when the backend tries to claim it.
      if (onboardingSessionId) {
        await preSyncOnboardingToServer(onboardingSessionId)
      }

      try {
        const me = await createUser({
          email,
          name,
          surname,
          phone,
          onboarding_session_id: onboardingSessionId || undefined,
        })
        console.info(
          ts(),
          `[auth][createUser] succeeded id=${me.id} onboarding_goal=${me.onboarding_goal || '(empty)'}`
        )
        captureRegisteredUser(me)
        return me
      } catch (createErr) {
        const createStatus = getErrorStatus(createErr)
        console.warn(ts(), `${modeLabel} createUser failed, trying getMe fallback`, createErr)
        try {
          return await getMe()
        } catch {
          // If POST /users failed because the user cap was reached and the
          // user doesn't exist in the DB yet, propagate the cap-reached error
          // so callers can redirect to the waitlist instead of showing a
          // generic failure.
          if (createStatus === 409) {
            console.error(ts(), `${modeLabel} user cap reached, no DB record exists`)
            throw createErr
          }
          console.error(ts(), `POST /users error after ${Date.now() - _tCreate}ms`, createErr)
          throw createErr
        }
      }
    },
    [captureRegisteredUser]
  )

  const loadOrCreateUser = useCallback(
    async (options: {
      email: string
      name?: string
      surname?: string
      phone?: string
    }): Promise<ApiUser> => {
      const { email, name, surname, phone } = options

      // Always check if the user already exists first — existing users
      // should be able to sign in regardless of the user cap.
      try {
        return await getMe()
      } catch (err) {
        if (getErrorStatus(err) !== 404) throw err
      }

      // User exists in Cognito but not in DB (e.g. orphaned identity after a
      // failed DB insert). Self-heal by creating the DB row.  POST /users is
      // idempotent — the backend returns the existing user if one already
      // exists for this cognito_id, and canSkipUserCapCheck() bypasses the
      // user cap for known Cognito identities.
      return createUserWithGetMeFallback({
        email,
        name,
        surname,
        phone,
        modeLabel: '[auth]',
      })
    },
    [createUserWithGetMeFallback]
  )

  const claimPendingOnboardingSession = useCallback(
    // eslint-disable-next-line complexity
    async (me: ApiUser): Promise<ApiUser> => {
      const onboardingSessionId = await storage.get(SESSION_ID_KEY)
      console.info(
        ts(),
        `[auth][claim] start sessionId=${onboardingSessionId ?? 'none'} userGoal=${me.onboarding_goal || '(empty)'}`
      )
      if (!onboardingSessionId) return me

      // If the user already has onboarding data (linked at signup time by
      // the backend CreateUser flow), just clear the local storage and
      // skip the separate claim round-trip.
      if (me.onboarding_goal) {
        console.info(ts(), '[auth][claim] onboarding already linked, clearing local storage')
        await storage.remove(SESSION_ID_KEY)
        await storage.remove(DATA_KEY)
        return me
      }

      const rawData = await storage.get(DATA_KEY)
      let localData: LocalOnboardingData | null = null

      if (rawData) {
        try {
          localData = JSON.parse(rawData) as LocalOnboardingData
        } catch (err) {
          console.warn('[auth] Invalid onboarding payload, clearing local copy', err)
          await storage.remove(DATA_KEY)
        }
      }

      if (localData) {
        console.info(ts(), '[auth][claim] patching onboarding session to server')
        try {
          await patchOnboardingSession(onboardingSessionId, {
            goal: localData.goal ? toEnum(GOAL_MAP, localData.goal) : undefined,
            destination: localData.destination
              ? toEnum(DESTINATION_MAP, localData.destination)
              : undefined,
            timeline: localData.timeline ? toEnum(TIMELINE_MAP, localData.timeline) : undefined,
            origin_city: localData.originCity ?? undefined,
            origin_country: localData.originCountry ?? undefined,
            age_range: localData.age ? toEnum(AGE_MAP, localData.age) : undefined,
            savings_status: localData.savings ? toEnum(SAVINGS_MAP, localData.savings) : undefined,
            current_situation: localData.situation
              ? toEnum(SITUATION_MAP, localData.situation)
              : undefined,
          })
        } catch (err) {
          console.warn('[auth][claim] Failed to sync onboarding payload before claim', err)
        }
      } else {
        console.info(ts(), '[auth][claim] no local onboarding data to sync')
      }

      console.info(ts(), '[auth][claim] calling claimOnboardingSession API')
      try {
        const claimed = await claimOnboardingSession(onboardingSessionId)
        console.info(
          ts(),
          `[auth][claim] success userId=${claimed.id} goal=${claimed.onboarding_goal || '(empty)'}`
        )
        await storage.remove(SESSION_ID_KEY)
        await storage.remove(DATA_KEY)
        capture('session_claimed', { session_id: onboardingSessionId })
        capture('onboarding_completed')
        return claimed
      } catch (err) {
        const status = getErrorStatus(err)
        if (status === 404 || status === 409) {
          console.warn(
            `[auth][claim] ${status === 404 ? 'session not found (404)' : 'conflict (409)'}, clearing local state`,
            err
          )
          await storage.remove(SESSION_ID_KEY)
          await storage.remove(DATA_KEY)
        } else {
          console.warn('[auth][claim] unexpected error, attempting getMe fallback', err)
        }

        // The claim may have partially succeeded (e.g. marked claimed_by on
        // the onboarding row but failed to set onboarding_id), or the session
        // was already linked in a previous attempt.  Re-fetch via GET /me,
        // which has a lateral-join fallback that finds onboarding data via
        // claimed_by even when onboarding_id is still NULL.
        try {
          const refetched = await getMe()
          console.info(
            ts(),
            `[auth][claim] getMe fallback goal=${refetched.onboarding_goal || '(empty)'}`
          )
          return refetched
        } catch {
          return me
        }
      }
    },
    [capture]
  )

  const resolveSessionUser = useCallback(
    async (options: ResolveSessionUserOptions): Promise<ApiUser> => {
      const { emailFromSession, name, surname, phone } = options

      if (emailFromSession?.trim()) {
        const effectiveEmail = requireEmailForBootstrap(emailFromSession)
        return loadOrCreateUser({
          email: effectiveEmail,
          name,
          surname,
          phone,
        })
      }

      console.warn(ts(), '[auth] missing email claim, trying existing user bootstrap')
      try {
        return await getMe()
      } catch (err) {
        if (getErrorStatus(err) === 404) {
          throw new Error('Your Cognito session is missing an email address.')
        }
        throw err
      }
    },
    [loadOrCreateUser]
  )

  const completeAuthenticatedSession = useCallback(
    async (options: CompleteSessionOptions): Promise<ApiUser> => {
      const { tokens, email, name, surname, phone } = options
      const _t0 = Date.now()
      const profile = getTokenProfile(tokens.idToken, tokens.accessToken)
      const effectiveName = name ?? profile.name ?? undefined
      const effectiveSurname = surname ?? profile.surname ?? undefined

      applySessionState(tokens)
      const seededUser = await resolveE2ESeededUser(tokens)
      let me =
        seededUser ??
        (await resolveSessionUser({
          emailFromSession: email ?? profile.email,
          name: effectiveName,
          surname: effectiveSurname,
          phone,
        }))

      me = await claimPendingOnboardingSession(me)
      setUser(me)

      return me
    },
    [applySessionState, claimPendingOnboardingSession, resolveSessionUser]
  )

  useEffect(() => {
    ;(async () => {
      try {
        const tokens = await readStoredTokensLight()
        if (!tokens) return
        await completeAuthenticatedSession({ tokens })
      } catch (err) {
        console.warn(ts(), 'session restore failed, clearing tokens', err)
        await clearStoredTokensLight()
        setAccessToken(null)
        setUser(null)
        setIsAdmin(false)
        setAuthMethod(null)
      } finally {
        setIsLoading(false)
      }
    })()
  }, [completeAuthenticatedSession])

  const signIn = useCallback(
    async (options: SignInOptions) => {
      const { email, password, name, surname, phone } = options
      console.info(ts(), '[auth][signIn] start')
      const { cognitoSignIn } = await import('@/lib/cognito')
      const tokens = await cognitoSignIn(email, password)
      return completeAuthenticatedSession({
        tokens,
        email,
        name,
        surname,
        phone,
      })
    },
    [completeAuthenticatedSession]
  )

  const signInWithGoogle = useCallback(async (): Promise<GoogleSignInResult> => {
    console.info(ts(), '[auth][google] signInWithGoogle: start')
    capture('google_oauth_debug', { prefix: '[auth][google]', step: 'sign_in_start' })
    try {
      const { startGoogleOAuth } = await import('@/lib/cognito-oauth')
      console.info(ts(), '[auth][google] signInWithGoogle: cognito-oauth module loaded')
      capture('google_oauth_debug', { prefix: '[auth][google]', step: 'module_loaded' })
      const result = await startGoogleOAuth()

      logGoogleOAuthResult(result, capture)

      if (result.type === 'cancelled' || result.type === 'redirected') {
        console.info(ts(), '[auth][google] signInWithGoogle: flow was ' + result.type)
        capture('google_oauth_debug', {
          prefix: '[auth][google]',
          step: 'flow_ended',
          type: result.type,
        })
        return result
      }

      console.info(ts(), '[auth][google] signInWithGoogle: completing authenticated session', {
        email: result.profile.email,
        name: result.profile.name,
        surname: result.profile.surname,
        authMethod: result.tokens.authMethod,
      })
      capture('google_oauth_debug', {
        prefix: '[auth][google]',
        step: 'completing_session',
        authMethod: result.tokens.authMethod,
      })
      const user = await completeAuthenticatedSession({
        tokens: result.tokens,
        email: result.profile.email,
        name: result.profile.name,
        surname: result.profile.surname,
      })
      console.info(ts(), '[auth][google] signInWithGoogle: success', {
        userId: user.id,
        email: user.email,
      })
      capture('google_oauth_debug', { prefix: '[auth][google]', step: 'sign_in_success' })
      return { type: 'success', user }
    } catch (err) {
      logGoogleOAuthError(err, capture)
      throw err
    }
  }, [completeAuthenticatedSession, capture])

  const completeGoogleCallback = useCallback(
    async (params: GoogleOAuthCallbackParams): Promise<ApiUser> => {
      console.info(ts(), '[auth][google] completeGoogleCallback: start', {
        hasCode: !!params.code,
        hasState: !!params.state,
        error: params.error,
        errorDescription: params.errorDescription,
      })
      capture('google_oauth_debug', {
        prefix: '[auth][google]',
        step: 'callback_start',
        hasCode: !!params.code,
        hasState: !!params.state,
        error: params.error,
        errorDescription: params.errorDescription,
      })
      try {
        const { completeGoogleOAuthCallback } = await import('@/lib/cognito-oauth')
        console.info(
          ts(),
          '[auth][google] completeGoogleCallback: module loaded, completing OAuth callback'
        )
        const { tokens, profile } = await completeGoogleOAuthCallback(params)
        console.info(ts(), '[auth][google] completeGoogleCallback: OAuth callback completed', {
          email: profile.email,
          hasTokens: !!tokens,
          authMethod: tokens.authMethod,
          name: profile.name,
          surname: profile.surname,
        })
        capture('google_oauth_debug', {
          prefix: '[auth][google]',
          step: 'callback_oauth_completed',
          hasTokens: !!tokens,
          authMethod: tokens.authMethod,
        })
        const user = await completeAuthenticatedSession({
          tokens,
          email: profile.email,
          name: profile.name,
          surname: profile.surname,
        })
        console.info(ts(), '[auth][google] completeGoogleCallback: session completed', {
          userId: user.id,
          email: user.email,
        })
        capture('google_oauth_debug', {
          prefix: '[auth][google]',
          step: 'callback_session_completed',
        })
        return user
      } catch (err) {
        console.error(ts(), '[auth][google] completeGoogleCallback: error', {
          error: err instanceof Error ? err.message : String(err),
          errorName: err instanceof Error ? err.name : 'Unknown',
          code: (err as { code?: string }).code,
          stack: err instanceof Error ? err.stack?.substring(0, 300) : undefined,
        })
        capture('google_oauth_debug', {
          prefix: '[auth][google]',
          step: 'callback_error',
          errorName: err instanceof Error ? err.name : 'Unknown',
          code: (err as { code?: string }).code,
        })
        throw err
      }
    },
    [completeAuthenticatedSession, capture]
  )

  const signUp = useCallback(async (email: string, password: string) => {
    const { cognitoSignUp } = await import('@/lib/cognito')
    await cognitoSignUp(email, password)
  }, [])

  const signOut = useCallback(async () => {
    const { cognitoSignOut } = await import('@/lib/cognito')
    await cognitoSignOut()
    await storage.remove(SESSION_ID_KEY)
    await storage.remove(DATA_KEY)
    setAccessToken(null)
    setUser(null)
    setIsAdmin(false)
    setAuthMethod(null)
  }, [])

  const adminSignIn = useCallback(
    async (email: string, password: string): Promise<AdminSignInResult> => {
      const { cognitoAdminSignIn } = await import('@/lib/cognito')
      const result = await cognitoAdminSignIn(email, password)

      if (result.type === 'tokens') {
        await completeAuthenticatedSession({
          tokens: result.tokens,
          email,
        })
        return result
      }

      return {
        type: 'mfa',
        challenge: {
          submitCode: async (code: string) => {
            const tokens = await result.challenge.submitCode(code)
            await completeAuthenticatedSession({
              tokens,
              email,
            })
            return tokens
          },
        },
      }
    },
    [completeAuthenticatedSession]
  )

  const updateUser = useCallback((updated: Partial<ApiUser>) => {
    setUser((prev) => (prev ? { ...prev, ...updated } : null))
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        isAdmin,
        authMethod,
        signIn,
        signInWithGoogle,
        completeGoogleCallback,
        adminSignIn,
        signUp,
        signOut,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
